import React, { useState } from 'react';
import { Star, Globe, ArrowRight, Users, TrendingUp, Award, Quote , Store} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from '../i18n/LanguageContext';

export default function DerrDigitalReferences({ darkMode = false }) {
  const { t } = useTranslation();
  const [activeFilter, setActiveFilter] = useState('alle');

  // Dynamische Klassen basierend auf dem Theme
  const bgColor = darkMode ? 'bg-gray-900' : 'bg-gray-50';
  const textColor = darkMode ? 'text-white' : 'text-gray-900';
  const cardBgColor = darkMode ? 'bg-gray-800' : 'bg-white';
  const accentColor = 'text-blue-500';
  const colorFade = 'bg-gradient-to-r from-blue-500 to-purple-600 text-transparent bg-clip-text';
  const buttonBgColor = darkMode ? 'bg-blue-500 hover:bg-blue-600' : 'bg-blue-500 hover:bg-blue-600';
  const borderColor = darkMode ? 'border-gray-700' : 'border-gray-200';

  const projects = [
    {
      id: 1,
      title: "Siemer Landmaschinen GmbH",
      category: "Business Site",
      image: "from-green-400 to-green-600",
      description: "Vollständige Business-Website mit integriertem Kontaktformular und Kundenportal.",
      results: ["50% weniger Verwaltungsaufwand", "99.9% Verfügbarkeit"],
      technologies: ["React", "Node.js", "nodemailer"],
      icon: Globe
    },
        {
      id: 2,
      title: "Partyvermietung",
      category: "Webshop",
      image: "from-blue-400 to-violet-600",
      description: "Vollständige E-Commerce Lösung und integriertem Lagerverwaltungssystem.",
      results: ["200% Umsatzsteigerung","50% weniger Verwaltungsaufwand", "99.9% Verfügbarkeit"],
      technologies: ["React", "Node.js", "MySQL", "Nodemailer"],
      icon: Store
    }
  ];

  const testimonials = [
    {
      name: "Bernd Siemer",
      company: "Siemer Landmaschinen GmbH",
      text: "Die Zusammenarbeit mit DerrDigital war unkompliziert und professionell – unsere neue Website ist modern, schnell und genau auf unsere Bedürfnisse zugeschnitten. Besonders das integrierte Kontaktformular erleichtert uns den Austausch mit Kunden erheblich.",
      rating: 5,
      avatar: "from-blue-400 to-blue-600"
    },
        {
      name: "Anonym",
      company: "Partyvermietung GbR",
      text: "Dank DerrDigital haben wir jetzt einen übersichtlichen und benutzerfreundlichen Online-Shop, über den unsere Kunden Partyzubehör ganz einfach mieten können. Die moderne E-Commerce-Lösung spart uns Zeit und bringt spürbar mehr Buchungen.",
      rating: 5,
      avatar: "from-blue-400 to-violet-600"
    }
  ];

  const filteredProjects = activeFilter === 'alle' 
    ? projects 
    : projects.filter(project => project.category === activeFilter);

  const filters = [
    { key: 'alle', label: t('references.filterAll') },
    { key: 'website', label: t('references.filterWebsites') },
    { key: 'webshop', label: t('references.filterEcommerce') },
    { key: 'cms', label: t('references.filterCms') },
    { key: 'individuell', label: t('references.filterCustom') }
  ];

  return (
    <div className={`min-h-screen ${bgColor} ${textColor} transition-colors duration-300`}>
      
      {/* Hero-Bereich */}
      <section className={`py-20 ${darkMode ? 'bg-gray-850' : 'bg-gray-100'} transition-colors duration-300`}>
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
            {t('references.heroTitle')} <span className={colorFade}>{t('references.heroHighlight')}</span>
          </h1>
          <p className="text-xl mb-8 max-w-3xl mx-auto">
            {t('references.heroDesc')}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto mt-12">
            <div className={`${cardBgColor} rounded-xl p-6 border ${borderColor}`}>
              <Users size={40} className={accentColor + " mx-auto mb-4"} />
              <h3 className="text-2xl font-bold mb-2">50+</h3>
              <p>{t('references.statCustomers')}</p>
            </div>
            <div className={`${cardBgColor} rounded-xl p-6 border ${borderColor}`}>
              <TrendingUp size={40} className={accentColor + " mx-auto mb-4"} />
              <h3 className="text-2xl font-bold mb-2">200%</h3>
              <p>{t('references.statRevenue')}</p>
            </div>
            <div className={`${cardBgColor} rounded-xl p-6 border ${borderColor}`}>
              <Award size={40} className={accentColor + " mx-auto mb-4"} />
              <h3 className="text-2xl font-bold mb-2">100%</h3>
              <p>{t('references.statSatisfaction')}</p>
              <p className="text-[10px] italic text-gray-500">*noch</p>
            </div>
          </div>
        </div>
      </section>

      {/* Filter */}
      <section className="py-8">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap justify-center gap-4">
            {filters.map((filter) => (
              <button
                key={filter.key}
                onClick={() => setActiveFilter(filter.key)}
                className={`px-6 py-3 rounded-lg border transition-all duration-300 ${
                  activeFilter === filter.key
                    ? buttonBgColor + ' text-white border-transparent'
                    : `${cardBgColor} ${borderColor} hover:border-blue-500`
                }`}
              >
                {filter.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Projekte */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredProjects.map((project) => {
              const IconComponent = project.icon;
              return (
                <div key={project.id} className={`${cardBgColor} rounded-xl shadow-lg overflow-hidden border ${borderColor} transition-all duration-300 hover:scale-105 hover:shadow-xl`}>
                  <div className={`h-48 bg-gradient-to-r ${project.image} flex items-center justify-center`}>
                    <IconComponent size={60} className="text-white" />
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold mb-3">{project.title}</h3>
                    <p className={`mb-4 ${darkMode ? "text-gray-300" : "text-gray-600"}`}>
                      {project.description}
                    </p>
                    
                    <div className="mb-4">
                      <h4 className="font-semibold mb-2">{t('references.resultsLabel')}</h4>
                      <ul className="space-y-1">
                        {project.results.map((result, index) => (
                          <li key={index} className={`text-sm ${accentColor} flex items-center`}>
                            <TrendingUp size={12} className="mr-2" />
                            {result}
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="mb-4">
                      <h4 className="font-semibold mb-2">{t('references.techLabel')}</h4>
                      <div className="flex flex-wrap gap-2">
                        {project.technologies.map((tech, index) => (
                          <span key={index} className={`text-xs px-2 py-1 rounded ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                            {tech}
                          </span>
                        ))}
                      </div>
                    </div>

                    <button className={`${buttonBgColor} text-white px-4 py-2 rounded-lg flex items-center w-full justify-center`}>
                      {t('references.detailsBtn')} <ArrowRight size={16} className="ml-2" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Kundenstimmen */}
      <section className={`py-20 ${darkMode ? 'bg-gray-850' : 'bg-gray-100'} transition-colors duration-300`}>
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold mb-4">{t('references.testimonialsTitle')}</h2>
            <p className="max-w-2xl mx-auto text-lg">
              {t('references.testimonialsDesc')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className={`${cardBgColor} rounded-xl shadow-lg p-6 border ${borderColor} relative`}>
                <Quote size={40} className={`${accentColor} mb-4`} />
                <p className={`mb-6 ${darkMode ? "text-gray-300" : "text-gray-600"} italic`}>
                  "{testimonial.text}"
                </p>
                
                <div className="flex items-center mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} size={16} className="text-yellow-400 fill-current" />
                  ))}
                </div>

                <div className="flex items-center">
                  <div className={`w-12 h-12 rounded-full bg-gradient-to-r ${testimonial.avatar} flex items-center justify-center text-white font-bold mr-4`}>
                    {testimonial.name.split(' ').map(n => n[0]).join('')}
                  </div>
                  <div>
                    <p className="font-semibold">{testimonial.name}</p>
                    <p className={`text-sm ${darkMode ? "text-gray-400" : "text-gray-500"}`}>
                      {testimonial.company}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Erfolgszahlen */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold mb-4">{t('references.statsTitle')}</h2>
            <p className="max-w-2xl mx-auto text-lg">
              {t('references.statsDesc')}
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-4xl font-bold mb-2 bg-gradient-to-r from-blue-500 to-purple-600 text-transparent bg-clip-text">
                300%
              </div>
              <p className={darkMode ? "text-gray-300" : "text-gray-600"}>
                {t('references.statRevLabel')}
              </p>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold mb-2 bg-gradient-to-r from-green-500 to-teal-600 text-transparent bg-clip-text">
                99.9%
              </div>
              <p className={darkMode ? "text-gray-300" : "text-gray-600"}>
                {t('references.statUptimeLabel')}
              </p>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold mb-2 bg-gradient-to-r from-orange-500 to-red-600 text-transparent bg-clip-text">
                80%
              </div>
              <p className={darkMode ? "text-gray-300" : "text-gray-600"}>
                {t('references.statEffLabel')}
              </p>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold mb-2 bg-gradient-to-r from-purple-500 to-pink-600 text-transparent bg-clip-text">
                24/7
              </div>
              <p className={darkMode ? "text-gray-300" : "text-gray-600"}>
                {t('references.statSupportLabel')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA-Bereich */}
      <section className="py-20 bg-gradient-to-r from-blue-500 to-purple-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            {t('references.ctaTitle')}
          </h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            {t('references.ctaDesc')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link className="bg-white text-blue-600 px-6 py-3 rounded-lg shadow-lg font-bold"
            to="/kontakt">
              {t('references.ctaButton')}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}